import { useState, useEffect } from 'react';
import { useContentManager } from '../../../hooks/useContentManager';
import type { ContentItem } from '../../../hooks/useContentManager';
import MediaUploader from '../../../components/admin/MediaUploader';
import { extractGoogleMapsSrc } from '../../brand/components/BrandLocation';
import { api } from '../../../lib/api';

export default function BrandContentManager() {
  const { content, loading, updateContentItem, deleteContentItem, reloadContent, getContentItem } = useContentManager();
  const [activeModule, setActiveModule] = useState<string>('brand-hero');
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Vision Section için state'ler
  const [visionFormData, setVisionFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    image_url: ''
  });

  // Story Section için state'ler
  const [storyFormData, setStoryFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    image_url: ''
  });

  // Location & Surroundings için state'ler
  const [locationFormData, setLocationFormData] = useState({
    map_url: '',
    image_url: '',
    description: ''
  });

  // Architectural Design state'leri
  const [editingArchitectural, setEditingArchitectural] = useState<any | null>(null);
  const [architecturalFormData, setArchitecturalFormData] = useState({
    title: '',
    images: [] as string[]
  });

  // Vision içeriğini yükle
  useEffect(() => {
    const visionContent = getContentItem('brand', 'vision');
    if (visionContent?.metadata) {
      setVisionFormData(visionContent.metadata);
    }
  }, [content]);

  // Brand Contact Section Module (top-level)
  function renderBrandContactSectionModule() {
    const contactTitle = getContentItem('brand', 'contact-title');
    const contactLinks = getContentItem('brand', 'contact-links');
    const contactSocial = getContentItem('brand', 'contact-social');
    const contactIcons = getContentItem('brand', 'contact-icons');
    const contactForm = getContentItem('brand', 'contact-form');

    const saveTitle = async () => {
      const value = (document.getElementById('brand-contact-title') as HTMLInputElement)?.value || '';
      const item: ContentItem = {
        id: contactTitle?.id || `temp-brand-contact-title-${Date.now()}`,
        page: 'brand',
        section: 'contact-title',
        type: 'text',
        content: value,
        order: 0
      } as any;
      await updateContentItem(item);
      await reloadContent();
      alert('Title saved successfully');
    };

    const saveLinks = async () => {
      const link1_text = (document.getElementById('brand-link1-text') as HTMLInputElement)?.value || '';
      const link1_url = (document.getElementById('brand-link1-url') as HTMLInputElement)?.value || '';
      const link2_text = (document.getElementById('brand-link2-text') as HTMLInputElement)?.value || '';
      const link2_url = (document.getElementById('brand-link2-url') as HTMLInputElement)?.value || '';
      const item: ContentItem = {
        id: contactLinks?.id || `temp-brand-contact-links-${Date.now()}`,
        page: 'brand',
        section: 'contact-links',
        type: 'links',
        content: '',
        order: 0,
        metadata: { link1_text, link1_url, link2_text, link2_url }
      } as any;
      await updateContentItem(item);
      await reloadContent();
      alert('Links saved successfully');
    };

    const saveSocial = async () => {
      const instagram = (document.getElementById('brand-social-instagram') as HTMLInputElement)?.value || '';
      const facebook = (document.getElementById('brand-social-facebook') as HTMLInputElement)?.value || '';
      const youtube = (document.getElementById('brand-social-youtube') as HTMLInputElement)?.value || '';
      const linkedin = (document.getElementById('brand-social-linkedin') as HTMLInputElement)?.value || '';
      const snapchat = (document.getElementById('brand-social-snapchat') as HTMLInputElement)?.value || '';
      const item: ContentItem = {
        id: contactSocial?.id || `temp-brand-contact-social-${Date.now()}`,
        page: 'brand',
        section: 'contact-social',
        type: 'social',
        content: '',
        order: 0,
        metadata: { instagram, facebook, youtube, linkedin, snapchat }
      } as any;
      await updateContentItem(item);
      await reloadContent();
      alert('Social media saved successfully');
    };

    const saveIcons = async () => {
      const phone = (document.getElementById('brand-icons-phone') as HTMLInputElement)?.value || '';
      const whatsapp = (document.getElementById('brand-icons-whatsapp') as HTMLInputElement)?.value || '';
      const email = (document.getElementById('brand-icons-email') as HTMLInputElement)?.value || '';
      const pdf_url = (document.getElementById('brand-icons-pdf') as HTMLInputElement)?.value || '';
      const item: ContentItem = {
        id: contactIcons?.id || `temp-brand-contact-icons-${Date.now()}`,
        page: 'brand',
        section: 'contact-icons',
        type: 'icons',
        content: '',
        order: 0,
        metadata: { phone, whatsapp, email, pdf_url }
      } as any;
      await updateContentItem(item);
      await reloadContent();
      alert('Icons saved successfully');
    };

    const saveForm = async () => {
      const form_title = (document.getElementById('brand-form-title') as HTMLInputElement)?.value || '';
      const submit_button_text = (document.getElementById('brand-form-submit') as HTMLInputElement)?.value || '';
      const default_country_code = (document.getElementById('brand-form-country') as HTMLInputElement)?.value || '+233';
      const item: ContentItem = {
        id: contactForm?.id || `temp-brand-contact-form-${Date.now()}`,
        page: 'brand',
        section: 'contact-form',
        type: 'form_settings',
        content: 'Form Settings',
        order: 0,
        metadata: { form_title, submit_button_text, default_country_code }
      } as any;
      await updateContentItem(item);
      await reloadContent();
      alert('Form settings saved successfully');
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Left Title (Multi-line supported)</h3>
          <input id="brand-contact-title" defaultValue={contactTitle?.content || 'EXPRESS\nYOUR\nINTEREST'} className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white" />
          <div className="mt-3">
            <button onClick={saveTitle} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Left Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input id="brand-link1-text" placeholder="Link 1 Text" defaultValue={contactLinks?.metadata?.link1_text || 'FIND A SALES BOUTIQUE'} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-link1-url" placeholder="Link 1 URL" defaultValue={contactLinks?.metadata?.link1_url || '#'} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-link2-text" placeholder="Link 2 Text" defaultValue={contactLinks?.metadata?.link2_text || 'DISCOVER THE COLLECTION'} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-link2-url" placeholder="Link 2 URL" defaultValue={contactLinks?.metadata?.link2_url || '#'} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div className="mt-3">
            <button onClick={saveLinks} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input id="brand-social-instagram" placeholder="Instagram URL" defaultValue={contactSocial?.metadata?.instagram || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-social-facebook" placeholder="Facebook URL" defaultValue={contactSocial?.metadata?.facebook || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-social-youtube" placeholder="YouTube URL" defaultValue={contactSocial?.metadata?.youtube || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-social-linkedin" placeholder="LinkedIn URL" defaultValue={contactSocial?.metadata?.linkedin || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-social-snapchat" placeholder="X (Twitter) URL" defaultValue={contactSocial?.metadata?.snapchat || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div className="mt-3">
            <button onClick={saveSocial} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Form Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input id="brand-form-title" placeholder="Form Title" defaultValue={contactForm?.metadata?.form_title || 'REQUEST A CALLBACK'} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-form-submit" placeholder="Button Text" defaultValue={contactForm?.metadata?.submit_button_text || 'MEET OUR BRAND AMBASSADOR'} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-form-country" placeholder="Default Country Code" defaultValue={contactForm?.metadata?.default_country_code || '+233'} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div className="mt-3">
            <button onClick={saveForm} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Floating Action Icons</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input id="brand-icons-phone" placeholder="Phone" defaultValue={contactIcons?.metadata?.phone || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-icons-whatsapp" placeholder="WhatsApp" defaultValue={contactIcons?.metadata?.whatsapp || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-icons-email" placeholder="Email" defaultValue={contactIcons?.metadata?.email || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
            <input id="brand-icons-pdf" placeholder="PDF URL" defaultValue={contactIcons?.metadata?.pdf_url || ''} className="px-3 py-2 border rounded-md text-gray-900 bg-white" />
          </div>
          <div className="mt-3">
            <button onClick={saveIcons} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer">Save</button>
          </div>
        </div>
      </div>
    );
  }

  // Story içeriğini yükle
  useEffect(() => {
    const storyContent = getContentItem('brand', 'story');
    if (storyContent?.metadata) {
      setStoryFormData(storyContent.metadata);
    }
  }, [content]);

  // Location & Surroundings içeriğini yükle
  useEffect(() => {
    const locationContent = getContentItem('brand', 'location-surroundings');
    if (locationContent?.metadata) {
      setLocationFormData({
        map_url: locationContent.metadata.map_url || '',
        image_url: locationContent.metadata.image_url || '',
        description: locationContent.metadata.description || ''
      });
    }
  }, [content]);

  const brandModules = [
    { id: 'brand-hero', name: '1. Hero Video', icon: 'ri-video-line' },
    { id: 'brand-hero-text', name: '1. Hero Text & Buttons', icon: 'ri-font-size' },
    { id: 'brand-about', name: '2. About Section', icon: 'ri-information-line' },
    { id: 'brand-residences', name: '3. Residence Section', icon: 'ri-home-4-line' },
    { id: 'brand-location-new', name: '4. Location Section', icon: 'ri-map-pin-line' },
    { id: 'brand-amenities', name: '5. Amenities Section', icon: 'ri-sparkling-line' },
    { id: 'brand-investment', name: '6. Investment & Form', icon: 'ri-money-dollar-circle-line' },
    { id: 'brand-contact', name: 'Contact & Social', icon: 'ri-contacts-line' }
  ];

  // ── 1. Hero Text & Buttons Component ─────────────────────────────
  const BrandHeroTextEditor = ({
    item,
    onSave
  }: {
    item?: ContentItem;
    onSave: (payload: any) => Promise<void>;
  }) => {
    const defaults = {
      logo_url: item?.metadata?.logo_url || 'https://cdn.berkongroup.com/media/images/png%20logo%20golden_1765904823509.webp',
      title_line1: item?.metadata?.title_line1 || 'Prestigious Address,',
      title_bold: item?.metadata?.title_bold || 'Prestigious Living',
      subtitle: item?.metadata?.subtitle || '',
      button_text: item?.metadata?.button_text || 'DOWNLOAD CATALOGUE',
      button_url: item?.metadata?.button_url || '/catalog.pdf',
      button2_text: item?.metadata?.button2_text || 'EXPLORE RESIDENCES',
      button2_url: item?.metadata?.button2_url || '#curated-residences'
    };

    const [heroLogo, setHeroLogo] = useState<string>(defaults.logo_url);
    const [titleLine1, setTitleLine1] = useState<string>(defaults.title_line1);
    const [titleBold, setTitleBold] = useState<string>(defaults.title_bold);
    const [subtitle, setSubtitle] = useState<string>(defaults.subtitle);
    const [btnText, setBtnText] = useState<string>(defaults.button_text);
    const [btnUrl, setBtnUrl] = useState<string>(defaults.button_url);
    const [btn2Text, setBtn2Text] = useState<string>(defaults.button2_text);
    const [btn2Url, setBtn2Url] = useState<string>(defaults.button2_url);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (item?.metadata) {
        if (item.metadata.logo_url !== undefined) setHeroLogo(item.metadata.logo_url);
        if (item.metadata.title_line1 !== undefined) setTitleLine1(item.metadata.title_line1);
        if (item.metadata.title_bold !== undefined) setTitleBold(item.metadata.title_bold);
        if (item.metadata.subtitle !== undefined) setSubtitle(item.metadata.subtitle);
        if (item.metadata.button_text !== undefined) setBtnText(item.metadata.button_text);
        if (item.metadata.button_url !== undefined) setBtnUrl(item.metadata.button_url);
        if (item.metadata.button2_text !== undefined) setBtn2Text(item.metadata.button2_text);
        if (item.metadata.button2_url !== undefined) setBtn2Url(item.metadata.button2_url);
      }
    }, [item]);

    const handleSave = async () => {
      setIsSaving(true);
      try {
        await onSave({
          logo_url: heroLogo,
          title_line1: titleLine1,
          title_bold: titleBold,
          subtitle: subtitle,
          button_text: btnText,
          button_url: btnUrl,
          button2_text: btn2Text,
          button2_url: btn2Url
        });
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Hero Text, Logo & Buttons</h3>

        {/* Golden Tower Exclusive Logo */}
        <div className="mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
          <label className="block text-sm font-semibold text-gray-900 mb-1">
            Golden Tower Sayfasına Özel Logo
          </label>
          <p className="text-xs text-gray-500 mb-3">
            "Prestigious Address, Prestigious Living" başlığının üstünde görüntülenir.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            {heroLogo && (
              <div className="w-36 h-20 rounded-lg bg-[#0a0a0f] p-2 flex items-center justify-center border border-gray-700 shrink-0">
                <img src={heroLogo} alt="Logo Preview" className="max-h-full max-w-full object-contain" />
              </div>
            )}
            <div className="flex-1 w-full space-y-2">
              <input
                id="brand-hero-logo"
                type="text"
                value={heroLogo}
                onChange={(e) => setHeroLogo(e.target.value)}
                placeholder="Logo URL"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs font-mono text-gray-900 bg-white"
              />
              <MediaUploader
                onUploadSuccess={(url) => setHeroLogo(url)}
                label="Yeni Logo Yükle (PNG / WebP)"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (Line 1)</label>
            <input
              type="text"
              value={titleLine1}
              onChange={(e) => setTitleLine1(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title (Bold / Accent)</label>
            <input
              type="text"
              value={titleBold}
              onChange={(e) => setTitleBold(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">1st Button Text</label>
            <input
              type="text"
              value={btnText}
              onChange={(e) => setBtnText(e.target.value)}
              placeholder="e.g. DOWNLOAD CATALOGUE"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">1st Button URL</label>
            <input
              type="text"
              value={btnUrl}
              onChange={(e) => setBtnUrl(e.target.value)}
              placeholder="e.g. /catalog.pdf"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">2nd Button Text</label>
            <input
              type="text"
              value={btn2Text}
              onChange={(e) => setBtn2Text(e.target.value)}
              placeholder="e.g. EXPLORE RESIDENCES"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">2nd Button URL (Sayfa İçi Hedef: #curated-residences)</label>
            <input
              type="text"
              value={btn2Url}
              onChange={(e) => setBtn2Url(e.target.value)}
              placeholder="#curated-residences"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
        </div>
        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors cursor-pointer disabled:opacity-50"
          >
            {isSaving ? 'Kaydediliyor...' : 'Save'}
          </button>
        </div>
      </div>
    );
  };

  const renderBrandHeroTextModule = () => {
    const item = getContentItem('brand', 'hero-text');
    const handleSave = async (metadata: any) => {
      const payload: ContentItem = {
        id: item?.id || `temp-brand-hero-text-${Date.now()}`,
        page: 'brand',
        section: 'hero-text',
        type: 'text',
        content: 'Brand Hero Text',
        order: 0,
        metadata: {
          ...(item?.metadata || {}),
          ...metadata
        }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Brand Hero Text saved successfully');
    };

    return <BrandHeroTextEditor key={item?.id || 'brand-hero-text-editor'} item={item} onSave={handleSave} />;
  };

  // ── 2. About Section Module ─────────────────────────────────────
  const AboutSectionEditor = ({
    item,
    onSave
  }: {
    item?: ContentItem;
    onSave: (payload: { heading: string; copy: string; images: string[] }) => Promise<void>;
  }) => {
    const defaultImages = [
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80'
    ];

    const [heading, setHeading] = useState<string>(
      item?.metadata?.heading || 'Golden Tower: Airport Residential'
    );
    const [copy, setCopy] = useState<string>(
      item?.metadata?.copy || "Golden Tower Ghana is a luxurious residence in Accra’s most prestigious address, Airport Residential. The development stands tall at 18 floors, delivering unparalleled views of prime Accra. Escape into our superior and expansive amenities that elevate your living even further. Choose a home that delivers on all facets of an investment. Welcome to Golden Tower, Accra’s most prestigious residence."
    );
    const [images, setImages] = useState<string[]>(() => {
      if (Array.isArray(item?.metadata?.images) && item.metadata.images.length > 0) {
        return item.metadata.images;
      }
      return defaultImages;
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (item?.metadata) {
        if (item.metadata.heading) setHeading(item.metadata.heading);
        if (item.metadata.copy) setCopy(item.metadata.copy);
        if (Array.isArray(item.metadata.images) && item.metadata.images.length > 0) {
          setImages(item.metadata.images);
        }
      }
    }, [item]);

    const handleRemoveImage = (index: number) => {
      setImages(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleAddImage = (url: string) => {
      setImages(prev => [...prev, url]);
    };

    const handleSaveClick = async () => {
      setIsSaving(true);
      try {
        await onSave({ heading, copy, images });
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="bg-white rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">2. About Section Management</h3>
            <p className="text-xs text-gray-500 mt-1">Manage headline, copy, and slideshow photos</p>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
          <input
            type="text"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            placeholder="Heading"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Body Copy</label>
          <textarea
            rows={5}
            value={copy}
            onChange={(e) => setCopy(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white leading-relaxed"
            placeholder="Body copy..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Right Column Slideshow Images</label>
          
          <div className="mb-4">
            <MediaUploader
              type="image"
              onUploadComplete={handleAddImage}
              label="Upload New Slideshow Image"
            />
          </div>

          {images.length === 0 ? (
            <div className="p-6 border-2 border-dashed rounded-lg text-center text-gray-400 text-sm">
              No images in slideshow. Upload an image above to add images.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {images.map((img: string, idx: number) => (
                <div key={idx} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50 shadow-sm">
                  <img
                    src={img}
                    alt=""
                    className="w-full h-32 object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80';
                    }}
                  />
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
                    #{idx + 1}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveImage(idx);
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors cursor-pointer shadow-md"
                    title="Delete Image"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 border-t flex justify-end">
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSaveClick}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-md transition-colors cursor-pointer flex items-center gap-2"
          >
            {isSaving && <i className="ri-loader-4-line animate-spin"></i>}
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    );
  };

  const renderAboutModule = () => {
    const item = getContentItem('brand', 'about');

    const handleSave = async (payloadData: { heading: string; copy: string; images: string[] }) => {
      const payload: ContentItem = {
        id: item?.id || `temp-brand-about-${Date.now()}`,
        page: 'brand',
        section: 'about',
        type: 'about',
        content: 'Brand About Section',
        order: 1,
        metadata: {
          ...(item?.metadata || {}),
          heading: payloadData.heading,
          copy: payloadData.copy,
          images: payloadData.images
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('About Section saved successfully');
    };

    return <AboutSectionEditor key={item?.id || 'about-editor'} item={item} onSave={handleSave} />;
  };

  // ── 3. Residence Section Module ─────────────────────────────────
  const ResidencesSectionEditor = ({
    item,
    onSave
  }: {
    item?: ContentItem;
    onSave: (payload: { description: string; units: any[] }) => Promise<void>;
  }) => {
    const defaultUnits = [
      {
        id: 'studios',
        name: 'Studios',
        title: 'Modern Luxury Studio Apartments',
        area: '48 m²',
        description: 'Intelligently planned urban residences offering maximized space, seamless flow and expansive balconies with breathtaking skyline views of Accra.',
        view_3d_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80',
        floor_plan_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80',
        images: [
          'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1000&q=80',
          'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1000&q=80'
        ],
        features: ['Open-plan Living & Sleeping', 'Designer Kitchenette', 'Luxury Bathroom', 'Private Balcony']
      },
      {
        id: '1-bed',
        name: '1 bed',
        title: 'Sophisticated 1 Bedroom Residences',
        area: '72 m²',
        description: 'Generously proportioned one-bedroom suites designed for sophisticated modern living with bespoke finishes and panoramic Airport Residential vistas.',
        view_3d_image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1000&q=80',
        floor_plan_image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1000&q=80',
        images: [
          'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1000&q=80',
          'https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1000&q=80'
        ],
        features: ['Master Bedroom with En-suite', 'Spacious Living & Dining', 'Fully Fitted European Kitchen', 'Private Terrace']
      },
      {
        id: '2-bed',
        name: '2 bed',
        title: 'Prestigious 2 Bedroom Suites',
        area: '115 m²',
        description: 'Sprawling two-bedroom residences featuring expansive entertaining spaces, dual private balconies, and dedicated dressing suites.',
        view_3d_image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1000&q=80',
        floor_plan_image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1000&q=80',
        images: [
          'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1000&q=80',
          'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80'
        ],
        features: ['2 En-suite Bedrooms', 'Powder Room for Guests', 'Expansive Dining & Lounge', 'Wraparound Balcony']
      },
      {
        id: '3-bed',
        name: '3 bed (upon request)',
        title: 'Exclusive 3 Bedroom Penthouses (Upon Request)',
        area: '185 m²',
        description: 'Rare limited-edition luxury suites offering bespoke layouts, double-height ceilings and panoramic corner views of prime Accra.',
        view_3d_image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=1000&q=80',
        floor_plan_image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=1000&q=80',
        images: [
          'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1000&q=80',
          'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1000&q=80'
        ],
        features: ['3 Grand En-suite Bedrooms', 'Private Lift Access', 'Maid’s Quarters', 'Expansive Skyline Terrace']
      }
    ];

    const [units, setUnits] = useState<any[]>(() => {
      if (Array.isArray(item?.metadata?.units) && item.metadata.units.length > 0) {
        return item.metadata.units.map((u: any, idx: number) => {
          const def = defaultUnits[idx] || defaultUnits.find(d => d.id === u.id) || {};
          return {
            ...def,
            ...u,
            view_3d_image: u.view_3d_image || u.floor_plan_image || def.view_3d_image || '',
            floor_plan_image: u.view_3d_image || u.floor_plan_image || def.floor_plan_image || '',
            images: Array.isArray(u.images) && u.images.length > 0 ? u.images : (def.images || []),
            features: Array.isArray(u.features) && u.features.length > 0 ? u.features : (def.features || [])
          };
        });
      }
      return defaultUnits;
    });

    const [introDescription, setIntroDescription] = useState<string>(
      item?.metadata?.description || "Golden Tower is made up of attractive home types, befitting our ideal homeowners' investment style and portfolio."
    );
    const [activeUnitIdx, setActiveUnitIdx] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (item?.metadata) {
        if (item.metadata.description) setIntroDescription(item.metadata.description);
        if (Array.isArray(item.metadata.units) && item.metadata.units.length > 0) {
          setUnits(item.metadata.units.map((u: any, idx: number) => {
            const def = defaultUnits[idx] || defaultUnits.find(d => d.id === u.id) || {};
            return {
              ...def,
              ...u,
              view_3d_image: u.view_3d_image || u.floor_plan_image || def.view_3d_image || '',
              floor_plan_image: u.view_3d_image || u.floor_plan_image || def.floor_plan_image || '',
              images: Array.isArray(u.images) && u.images.length > 0 ? u.images : (def.images || []),
              features: Array.isArray(u.features) && u.features.length > 0 ? u.features : (def.features || [])
            };
          }));
        }
      }
    }, [item]);

    const updateUnitField = (idx: number, field: string, value: any) => {
      setUnits(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        if (field === 'view_3d_image') {
          next[idx].floor_plan_image = value;
        }
        return next;
      });
    };

    const addPhotoToUnit = (unitIdx: number, url: string) => {
      setUnits(prev => {
        const next = [...prev];
        const existing = Array.isArray(next[unitIdx].images) ? next[unitIdx].images : [];
        next[unitIdx] = {
          ...next[unitIdx],
          images: [...existing, url]
        };
        return next;
      });
    };

    const removePhotoFromUnit = (unitIdx: number, photoIdx: number) => {
      setUnits(prev => {
        const next = [...prev];
        const existing = Array.isArray(next[unitIdx].images) ? next[unitIdx].images : [];
        next[unitIdx] = {
          ...next[unitIdx],
          images: existing.filter((_: any, i: number) => i !== photoIdx)
        };
        return next;
      });
    };

    const handleSaveClick = async () => {
      setIsSaving(true);
      try {
        await onSave({
          description: introDescription,
          units
        });
      } finally {
        setIsSaving(false);
      }
    };

    const cur = units[activeUnitIdx] || units[0];

    return (
      <div className="bg-white rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">3. Residence Section Management</h3>
            <p className="text-xs text-gray-500 mt-1">Manage Unit Types, 3D Views, and Photo Galleries</p>
          </div>
        </div>

        {/* Intro Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Section Intro Description</label>
          <textarea
            value={introDescription}
            onChange={(e) => setIntroDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            placeholder="Intro description..."
          />
        </div>

        {/* Unit Selector Tabs */}
        <div className="pt-2">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Select Residence Unit to Edit
          </label>
          <div className="flex flex-wrap gap-2 p-1.5 bg-gray-100 rounded-xl">
            {units.map((u, idx) => {
              const isSelected = activeUnitIdx === idx;
              return (
                <button
                  key={u.id || idx}
                  type="button"
                  onClick={() => setActiveUnitIdx(idx)}
                  className={`flex-1 min-w-[130px] px-4 py-2.5 rounded-lg font-semibold text-xs tracking-wider uppercase transition-all cursor-pointer flex flex-col items-center justify-center gap-1 ${
                    isSelected
                      ? 'bg-white text-blue-700 shadow-sm border border-gray-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/60'
                  }`}
                >
                  <span className="font-bold">{u.name}</span>
                  <span className="text-[10px] text-gray-400 font-normal">
                    {u.images?.length || 0} Photos • {u.view_3d_image ? '3D View ✓' : 'No 3D View'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Unit Management Card */}
        {cur && (
          <div className="p-6 border border-gray-200 rounded-2xl bg-gray-50/60 space-y-6">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold uppercase tracking-wider">
                  {cur.name}
                </span>
                <span className="text-xs text-gray-400 font-mono">ID: {cur.id}</span>
              </div>
            </div>

            {/* General Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Unit Headline Title
                </label>
                <input
                  type="text"
                  value={cur.title || ''}
                  onChange={(e) => updateUnitField(activeUnitIdx, 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white font-medium text-gray-900"
                  placeholder="e.g. Modern Luxury Studio Apartments"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Area (Size)
                </label>
                <input
                  type="text"
                  value={cur.area || ''}
                  onChange={(e) => updateUnitField(activeUnitIdx, 'area', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-900"
                  placeholder="e.g. 48 m²"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Unit Description
                </label>
                <textarea
                  rows={3}
                  value={cur.description || ''}
                  onChange={(e) => updateUnitField(activeUnitIdx, 'description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs bg-white text-gray-800 leading-relaxed"
                  placeholder="Enter unit description..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1">
                  Key Highlights / Features (Comma separated)
                </label>
                <input
                  type="text"
                  value={(cur.features || []).join(', ')}
                  onChange={(e) => {
                    const feats = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                    updateUnitField(activeUnitIdx, 'features', feats);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs bg-white text-gray-800"
                  placeholder="e.g. Open-plan Living, Designer Kitchenette, Luxury Bathroom, Private Balcony"
                />
              </div>
            </div>

            {/* SEPARATE MEDIA SECTIONS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
              
              {/* SECTION A: 3D VIEW IMAGE */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center font-bold text-xs">
                      3D
                    </span>
                    <div>
                      <h4 className="font-semibold text-sm text-gray-900">3D View Image</h4>
                      <p className="text-[11px] text-gray-500">Render / 3D model visual for {cur.name}</p>
                    </div>
                  </div>
                  {cur.view_3d_image && (
                    <button
                      type="button"
                      onClick={() => updateUnitField(activeUnitIdx, 'view_3d_image', '')}
                      className="text-xs text-red-600 hover:text-red-700 hover:underline cursor-pointer"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {cur.view_3d_image ? (
                  <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 aspect-video flex items-center justify-center group">
                    <img
                      src={cur.view_3d_image}
                      alt={`${cur.name} 3D View`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80';
                      }}
                    />
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded font-mono">
                      Current 3D View
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-gray-400 text-xs">
                    <i className="ri-layout-masonry-line text-3xl mb-1 block"></i>
                    No 3D View image uploaded yet.
                  </div>
                )}

                <MediaUploader
                  type="image"
                  compact={true}
                  label={cur.view_3d_image ? `Change 3D View for ${cur.name}` : `Upload 3D View for ${cur.name}`}
                  onUploadComplete={(url) => updateUnitField(activeUnitIdx, 'view_3d_image', url)}
                />

                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Direct Image URL</label>
                  <input
                    type="text"
                    value={cur.view_3d_image || ''}
                    onChange={(e) => updateUnitField(activeUnitIdx, 'view_3d_image', e.target.value)}
                    className="w-full px-2.5 py-1 text-xs border border-gray-300 rounded bg-gray-50 text-gray-700"
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* SECTION B: PHOTOS GALLERY */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-blue-100 text-blue-800 flex items-center justify-center font-bold text-xs">
                      <i className="ri-image-line"></i>
                    </span>
                    <div>
                      <h4 className="font-semibold text-sm text-gray-900">Photos Gallery</h4>
                      <p className="text-[11px] text-gray-500">Interior & exterior photos ({cur.images?.length || 0} uploaded)</p>
                    </div>
                  </div>
                </div>

                {cur.images && cur.images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-56 overflow-y-auto p-1 border border-gray-100 rounded-lg">
                    {cur.images.map((pImg: string, pIdx: number) => (
                      <div key={pIdx} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50 aspect-video shadow-xs">
                        <img
                          src={pImg}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=400&q=80';
                          }}
                        />
                        <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-mono">
                          #{pIdx + 1}
                        </div>
                        <button
                          type="button"
                          onClick={() => removePhotoFromUnit(activeUnitIdx, pIdx)}
                          className="absolute top-1 right-1 p-1 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors cursor-pointer shadow-sm"
                          title="Delete Photo"
                        >
                          <i className="ri-delete-bin-line text-[10px]"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-gray-400 text-xs">
                    <i className="ri-camera-lens-line text-3xl mb-1 block"></i>
                    No photos uploaded yet for this unit.
                  </div>
                )}

                <MediaUploader
                  type="image"
                  label={`Add Photo to ${cur.name} Gallery`}
                  onUploadComplete={(url) => addPhotoToUnit(activeUnitIdx, url)}
                />
              </div>

            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="pt-4 border-t flex justify-end">
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSaveClick}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-md transition-colors cursor-pointer flex items-center gap-2 shadow-sm"
          >
            {isSaving && <i className="ri-loader-4-line animate-spin"></i>}
            {isSaving ? 'Saving All Residences...' : 'Save All Residence Settings'}
          </button>
        </div>
      </div>
    );
  };

  const renderResidencesModule = () => {
    const item = getContentItem('brand', 'residences');

    const handleSave = async (payloadData: { description: string; units: any[] }) => {
      const payload: ContentItem = {
        id: item?.id || `temp-brand-residences-${Date.now()}`,
        page: 'brand',
        section: 'residences',
        type: 'residences',
        content: 'Brand Residences Section',
        order: 2,
        metadata: {
          ...(item?.metadata || {}),
          description: payloadData.description,
          units: payloadData.units
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Residences Section saved successfully');
    };

    return <ResidencesSectionEditor key={item?.id || 'residences-editor'} item={item} onSave={handleSave} />;
  };

  // ── 4. Location Section Component ────────────────────────────────
  const LocationSectionEditor = ({
    item,
    onSave
  }: {
    item?: ContentItem;
    onSave: (payload: any) => Promise<void>;
  }) => {
    const defaultCats = [
      {
        id: 'entertainment',
        name: 'Entertainment',
        icon: 'ri-goblet-line',
        items: ['Bliss Family Entertainment', 'Aviation Social Center', 'Accra Mall', '805 Restaurant', 'Polo Club Restaurant', 'Marina Mall']
      },
      {
        id: 'business',
        name: 'Business',
        icon: 'ri-building-line',
        items: ['Stanbic Bank', 'Absa', 'CalBank', 'Access', 'Standard Chartered', 'CBG', 'Fidelity Bank']
      },
      {
        id: 'education',
        name: 'Education',
        icon: 'ri-book-read-line',
        items: ['Safari Association Intl School', 'East Airport International School', 'École Française Adkel', 'Alsyd School']
      },
      {
        id: 'more',
        name: 'More',
        icon: 'ri-more-line',
        items: ['Nyaho Medical Center', 'Compu Ghana', 'Can West', 'CFAO', 'Stanbic Heights', 'Silver Star Towers']
      }
    ];

    const defaults = {
      to_airport_title: item?.metadata?.to_airport_title || 'To Airport',
      description: item?.metadata?.description || "Golden Tower is located in Accra’s most prestigious and celebrated neighbourhood, Airport Residential Area. Commuting from Golden Tower to the Accra International Airport via car takes 5 minutes, via bicycle, 8 minutes, and by jogging, 20 minutes. In addition, we are surrounded by industry-class hubs such as",
      car_time: item?.metadata?.car_time || '5 minutes',
      bicycle_time: item?.metadata?.bicycle_time || '8 minutes',
      jogging_time: item?.metadata?.jogging_time || '20 minutes',
      map_iframe: item?.metadata?.map_iframe || item?.metadata?.map_url || '',
      map_image: item?.metadata?.map_image || '',
      categories: Array.isArray(item?.metadata?.categories) && item!.metadata.categories.length > 0
        ? item!.metadata.categories
        : defaultCats
    };

    const [toAirportTitle, setToAirportTitle] = useState<string>(defaults.to_airport_title);
    const [description, setDescription] = useState<string>(defaults.description);
    const [carTime, setCarTime] = useState<string>(defaults.car_time);
    const [bicycleTime, setBicycleTime] = useState<string>(defaults.bicycle_time);
    const [joggingTime, setJoggingTime] = useState<string>(defaults.jogging_time);
    const [mapIframeInput, setMapIframeInput] = useState<string>(defaults.map_iframe);
    const [categories, setCategories] = useState<any[]>(defaults.categories);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      if (item?.metadata) {
        if (item.metadata.to_airport_title !== undefined) setToAirportTitle(item.metadata.to_airport_title);
        if (item.metadata.description !== undefined) setDescription(item.metadata.description);
        if (item.metadata.car_time !== undefined) setCarTime(item.metadata.car_time);
        if (item.metadata.bicycle_time !== undefined) setBicycleTime(item.metadata.bicycle_time);
        if (item.metadata.jogging_time !== undefined) setJoggingTime(item.metadata.jogging_time);
        if (item.metadata.map_iframe !== undefined || item.metadata.map_url !== undefined) {
          setMapIframeInput(item.metadata.map_iframe || item.metadata.map_url || '');
        }
        if (Array.isArray(item.metadata.categories) && item.metadata.categories.length > 0) {
          setCategories(item.metadata.categories);
        }
      }
    }, [item]);

    const handleSave = async () => {
      setIsSaving(true);
      try {
        const extractedUrl = extractGoogleMapsSrc(mapIframeInput);
        await onSave({
          to_airport_title: toAirportTitle,
          description,
          car_time: carTime,
          bicycle_time: bicycleTime,
          jogging_time: joggingTime,
          map_iframe: mapIframeInput,
          map_url: extractedUrl,
          map_image: defaults.map_image,
          categories
        });
      } finally {
        setIsSaving(false);
      }
    };

    const previewSrc = extractGoogleMapsSrc(mapIframeInput);

    return (
      <div className="bg-white rounded-lg p-6 space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">4. Location Section Management</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Location Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commute Subheading</label>
            <input
              type="text"
              value={toAirportTitle}
              onChange={(e) => setToAirportTitle(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
              placeholder="To Airport"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commute by Car</label>
            <input
              type="text"
              value={carTime}
              onChange={(e) => setCarTime(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commute by Bicycle</label>
            <input
              type="text"
              value={bicycleTime}
              onChange={(e) => setBicycleTime(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commute by Jogging / Walking</label>
            <input
              type="text"
              value={joggingTime}
              onChange={(e) => setJoggingTime(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
            />
          </div>
        </div>

        {/* Google Maps Embed / iframe code */}
        <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 space-y-3">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-semibold text-gray-900">
              Google Maps iframe Kodu
            </label>
            <span className="text-xs text-blue-600 font-medium">Google Maps Embed</span>
          </div>
          <p className="text-xs text-gray-500">
            Google Maps'ten konumu bulun &rarr; <b>Paylaş</b> &rarr; <b>Harita yerleştirme</b> sekmesine tıklayın &rarr; <b>HTML'yi Kopyala</b> diyerek çıkan <code className="bg-gray-200 px-1 py-0.5 rounded">&lt;iframe...&gt;</code> kodunu buraya yapıştırın.
          </p>
          <textarea
            rows={3}
            value={mapIframeInput}
            onChange={(e) => setMapIframeInput(e.target.value)}
            placeholder='<iframe src="https://www.google.com/maps/embed?pb=..." width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy"></iframe>'
            className="w-full px-3 py-2 font-mono text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
          />

          {/* Live Preview */}
          <div className="pt-2">
            <span className="text-xs font-medium text-gray-700 mb-1 block">Canlı Harita Önizleme:</span>
            <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-300 bg-gray-100 flex items-center justify-center">
              {previewSrc ? (
                <iframe
                  src={previewSrc}
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  title="Google Maps Preview"
                />
              ) : (
                <div className="text-sm text-gray-400 flex flex-col items-center gap-1 p-4 text-center">
                  <i className="ri-map-pin-line text-2xl text-gray-300"></i>
                  <span>Yukarıya bir Google Maps iframe kodu yapıştırdığınızda harita canlı olarak burada görüntülenecektir.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t">
          <h4 className="font-medium text-gray-900">Surroundings & Nearby Amenities</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((cat: any, idx: number) => (
              <div key={cat.id || idx} className="p-4 border rounded-lg bg-gray-50">
                <div className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <i className={`${cat.icon || 'ri-map-pin-line'} text-blue-600`}></i>
                  {cat.name}
                </div>
                <textarea
                  rows={4}
                  value={(cat.items || []).join('\n')}
                  onChange={(e) => {
                    const lines = e.target.value.split('\n');
                    setCategories((prev) => {
                      const updated = [...prev];
                      updated[idx] = { ...updated[idx], items: lines };
                      return updated;
                    });
                  }}
                  className="w-full text-xs p-2 border rounded bg-white text-gray-800"
                  placeholder="Enter one place per line"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors cursor-pointer disabled:opacity-50"
          >
            {isSaving ? 'Kaydediliyor...' : 'Save Location Settings'}
          </button>
        </div>
      </div>
    );
  };

  const renderLocationNewModule = () => {
    const item = getContentItem('brand', 'location');
    const handleSave = async (metadata: any) => {
      const payload: ContentItem = {
        id: item?.id || `temp-brand-location-${Date.now()}`,
        page: 'brand',
        section: 'location',
        type: 'location',
        content: 'Brand Location Section',
        order: 3,
        metadata: {
          ...(item?.metadata || {}),
          ...metadata
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Location Section saved successfully');
    };

    return <LocationSectionEditor key={item?.id || 'brand-location-editor'} item={item} onSave={handleSave} />;
  };

  // ── 5. Amenities Section Module ─────────────────────────────────
  const AmenitiesSectionEditor = ({
    item,
    onSave
  }: {
    item?: ContentItem;
    onSave: (items: any[]) => Promise<void>;
  }) => {
    const defaultAmenities = [
      {
        id: 'concierge',
        name: '24/7 Concierge & Security',
        description: 'Uninterrupted comfort and complete peace of mind — a lifestyle supported every hour of the day with refined service and exceptional care.',
        icon: 'ri-shield-check-line',
        image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'retail',
        name: 'Boutique Retail Experience',
        description: 'Luxury shopping just steps away curated with brands and concepts that complement an elevated lifestyle.',
        icon: 'ri-store-2-line',
        image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'coworking',
        name: 'Private Co-Working & Meeting Lounge',
        description: 'A sophisticated workspace where productivity meets elegance, complemented by an exclusive meeting room designed for focus, collaboration, and elevated professional gatherings.',
        icon: 'ri-building-2-line',
        image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'sports',
        name: 'Scenic Rooftop Multi-Purpose Sports Court',
        description: 'A versatile open-air sports court on the rooftop, enhanced by panoramic skyline views — a space where energy, performance, and breathtaking scenery come together.',
        icon: 'ri-basketball-line',
        image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'pool',
        name: 'Rooftop Sky Pool with Horizon Views',
        description: 'An open-air rooftop pool that blends breathtaking skyline vistas with a serene atmosphere — creating a refreshing escape high above the city.',
        icon: 'ri-drop-line',
        image: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'fitness',
        name: 'Where Energy Meets Performance',
        description: 'Crafted for balance, strength, and renewal — a space where performance meets serenity in an atmosphere designed to elevate your well-being.',
        icon: 'ri-fire-line',
        image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'spa',
        name: 'Private Spa & Sauna Retreat',
        description: 'A serene sanctuary for mind, body, and soul — offering moments of deep relaxation and thoughtful rejuvenation.',
        icon: 'ri-leaf-line',
        image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'gaming',
        name: 'Entertainment & Gaming Suite',
        description: 'A premium leisure destination featuring PlayStation zones and a refined billiards area — where entertainment transforms into an elevated, memorable experience.',
        icon: 'ri-gamepad-line',
        image: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'dining',
        name: 'Curated Fine Dining Experience',
        description: 'Exclusive dining spaces designed for refined tastes and intimate moments, offering a setting where gastronomy and elegance meet at the highest level.',
        icon: 'ri-restaurant-2-line',
        image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80'
      },
      {
        id: 'kids',
        name: "Dedicated Children's Play Area",
        description: 'A beautifully crafted environment designed exclusively for children — a safe, joyful world where little ones explore, learn, and play freely.',
        icon: 'ri-bear-smile-line',
        image: 'https://images.unsplash.com/photo-1566454544259-f4b94c3d758c?auto=format&fit=crop&w=800&q=80'
      }
    ];

    const [items, setItems] = useState<any[]>(() => {
      if (Array.isArray(item?.metadata?.items) && item.metadata.items.length > 0) {
        return item.metadata.items.map((it: any, idx: number) => {
          const fallback = defaultAmenities.find(d => d.id === it.id || d.name?.toLowerCase() === it.name?.toLowerCase()) || defaultAmenities[idx];
          return {
            ...it,
            description: it.description !== undefined ? it.description : (fallback?.description || '')
          };
        });
      }
      return defaultAmenities;
    });

    const [isSaving, setIsSaving] = useState(false);

    const updateItemField = (idx: number, field: string, value: any) => {
      setItems(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        return next;
      });
    };

    const handleAddItem = () => {
      const newItem = {
        id: `amenity-${Date.now()}`,
        name: 'New Amenity',
        description: 'Description of this amenity...',
        icon: 'ri-star-line',
        image: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?auto=format&fit=crop&w=800&q=80'
      };
      setItems(prev => [...prev, newItem]);
    };

    const handleDeleteItem = (idx: number) => {
      if (confirm('Are you sure you want to delete this amenity?')) {
        setItems(prev => prev.filter((_, i) => i !== idx));
      }
    };

    const handleSaveAll = async () => {
      setIsSaving(true);
      try {
        await onSave(items);
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="bg-white rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">5. Amenities Section Management</h3>
            <p className="text-xs text-gray-500 mt-1">Manage names, icons, descriptions, and uploaded images for each amenity.</p>
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            className="px-3.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Add New Amenity
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((it: any, idx: number) => (
            <div key={it.id || idx} className="p-4 border border-gray-200 rounded-xl bg-gray-50/70 hover:bg-gray-50 transition-colors flex flex-col sm:flex-row gap-4 items-start shadow-sm">
              {/* Image preview & uploader column */}
              <div className="w-full sm:w-36 flex flex-col gap-2 shrink-0">
                <div className="relative w-full aspect-video sm:aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                  <img
                    src={it.image || 'https://via.placeholder.com/300x200?text=No+Image'}
                    alt={it.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?auto=format&fit=crop&w=800&q=80';
                    }}
                  />
                  <div className="absolute top-1.5 right-1.5 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">
                    #{idx + 1}
                  </div>
                </div>

                <MediaUploader
                  type="image"
                  label="Upload Image"
                  currentUrl={it.image}
                  compact={true}
                  onUploadComplete={(url) => updateItemField(idx, 'image', url)}
                />
              </div>

              {/* Fields column */}
              <div className="flex-1 w-full space-y-2.5">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    Amenity Name
                  </label>
                  <input
                    type="text"
                    value={it.name || ''}
                    onChange={(e) => updateItemField(idx, 'name', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm bg-white font-medium text-gray-900 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Amenity Name"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    Description
                  </label>
                  <textarea
                    rows={2}
                    value={it.description || ''}
                    onChange={(e) => updateItemField(idx, 'description', e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white text-gray-700 leading-relaxed focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
                    placeholder="Enter descriptive text for this amenity..."
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    Image URL
                  </label>
                  <input
                    type="text"
                    value={it.image || ''}
                    onChange={(e) => updateItemField(idx, 'image', e.target.value)}
                    className="w-full px-2.5 py-1 border border-gray-300 rounded text-xs bg-white text-gray-600 focus:ring-1 focus:ring-blue-500"
                    placeholder="https://... or upload above"
                  />
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-gray-200">
                  <div className="flex items-center gap-1.5">
                    <span className="w-7 h-7 rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700 text-sm">
                      <i className={it.icon || 'ri-star-line'}></i>
                    </span>
                    <input
                      type="text"
                      value={it.icon || ''}
                      onChange={(e) => updateItemField(idx, 'icon', e.target.value)}
                      className="w-36 px-2 py-1 border border-gray-300 rounded text-xs bg-white text-gray-700 font-mono focus:ring-1 focus:ring-blue-500"
                      placeholder="ri-shield-check-line"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleDeleteItem(idx)}
                    className="text-xs text-red-600 hover:text-red-700 font-medium hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t flex justify-end">
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSaveAll}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-md transition-colors cursor-pointer flex items-center gap-2"
          >
            {isSaving && <i className="ri-loader-4-line animate-spin"></i>}
            {isSaving ? 'Saving...' : 'Save All Amenities'}
          </button>
        </div>
      </div>
    );
  };

  const renderAmenitiesModule = () => {
    const item = getContentItem('brand', 'amenities');

    const handleSave = async (updatedItems: any[]) => {
      const payload: ContentItem = {
        id: item?.id || `temp-brand-amenities-${Date.now()}`,
        page: 'brand',
        section: 'amenities',
        type: 'amenities',
        content: 'Brand Amenities Section',
        order: 4,
        metadata: {
          ...(item?.metadata || {}),
          items: updatedItems
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Amenities Section saved successfully');
    };

    return <AmenitiesSectionEditor key={item?.id || 'amenities-editor'} item={item} onSave={handleSave} />;
  };

  // ── 6. Investment & Form Module ─────────────────────────────────
  const renderInvestmentModule = () => {
    const item = getContentItem('brand', 'investment');
    const defaults = {
      heading: item?.metadata?.heading || 'Are you an investor looking to learn more about the investment returns Golden Tower provides?',
      subheading: item?.metadata?.subheading || 'Download Our Investor Guide Now',
      button_text: item?.metadata?.button_text || 'Download Guide',
      form_title: item?.metadata?.form_title || 'Register for Investor Details'
    };

    const handleSave = async () => {
      const payload: ContentItem = {
        id: item?.id || `temp-brand-investment-${Date.now()}`,
        page: 'brand',
        section: 'investment',
        type: 'investment',
        content: 'Brand Investment Section',
        order: 5,
        metadata: {
          ...(item?.metadata || {}),
          heading: (document.getElementById('brand-inv-heading') as HTMLTextAreaElement)?.value || defaults.heading,
          subheading: (document.getElementById('brand-inv-sub') as HTMLInputElement)?.value || defaults.subheading,
          button_text: (document.getElementById('brand-inv-btn') as HTMLInputElement)?.value || defaults.button_text,
          form_title: (document.getElementById('brand-inv-form-title') as HTMLInputElement)?.value || defaults.form_title
        }
      };
      await updateContentItem(payload);
      await reloadContent();
      alert('Investment & Form Section saved successfully');
    };

    return (
      <div className="bg-white rounded-lg p-6 space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">6. Investment Opportunities & Form Management</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Investor Heading</label>
          <textarea
            id="brand-inv-heading"
            defaultValue={defaults.heading}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subheading</label>
            <input
              id="brand-inv-sub"
              type="text"
              defaultValue={defaults.subheading}
              className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
            <input
              id="brand-inv-btn"
              type="text"
              defaultValue={defaults.button_text}
              className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Form Title</label>
          <input
            id="brand-inv-form-title"
            type="text"
            defaultValue={defaults.form_title}
            className="w-full px-3 py-2 border rounded-md text-gray-900 bg-white"
          />
        </div>

        <div className="p-4 bg-gray-50 border rounded-lg text-sm text-gray-600 space-y-1">
          <div className="font-semibold text-gray-800">HubSpot Form Integration:</div>
          <div>Form ID: <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">49087e52-a00a-4569-a9c1-0265a30255a1</code></div>
          <div>Portal ID: <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">147855768</code></div>
          <div className="text-xs text-gray-500 mt-1">When the form is successfully submitted, the system automatically opens/downloads the investor catalogue.</div>
        </div>

        <div className="pt-4 border-t">
          <button
            onClick={handleSave}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors cursor-pointer"
          >
            Save
          </button>
        </div>
      </div>
    );
  };  // Hero Video Module
  const renderHeroModule = () => {
    const heroVideo = getContentItem('brand', 'hero-video');
    const currentVideoUrl = heroVideo?.video_url || heroVideo?.content || '';

    const handleVideoUpload = async (file: File) => {
      try {
        setVideoUploading(true);
        setUploadProgress(20);
        
        const maxSize = 75 * 1024 * 1024;
        if (file.size > maxSize) {
          alert(`Video file is too large! Maximum allowed is 75MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
          setVideoUploading(false);
          return;
        }

        setUploadProgress(40);
        setUploadProgress(70);

        const data = await api.upload('/media/upload', file);
        if (!data.success) throw new Error(data.error || 'Video upload error');
        
        const videoUrl = data.url;
        setUploadProgress(90);
        
        const heroVideoItem = {
          id: heroVideo?.id || 'brand-hero-video',
          page: 'brand',
          section: 'hero-video',
          type: 'video',
          content: videoUrl,
          video_url: videoUrl,
          order: 0,
          title: 'Brand Hero Video',
          page_key: `brand-hero-video-${timestamp}`
        };

        await updateContentItem(heroVideoItem);
        
        setUploadProgress(100);
        await reloadContent();
        alert('Video uploaded successfully!');
        console.log('Video uploaded successfully:', videoUrl);
      } catch (error) {
        console.error('Video upload error:', error);
        alert('An error occurred during video upload!');
      } finally {
        setVideoUploading(false);
        setUploadProgress(0);
      }
    };

    return (
      <div className="space-y-6">
        {/* Hero Video Management */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <i className="ri-video-line mr-2"></i>
            Hero Video Module
          </h3>
          
          {/* Current Video URL */}
          {currentVideoUrl && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Video URL:
              </label>
              <div className="text-sm text-gray-600 break-all bg-white p-2 rounded border">
                {currentVideoUrl}
              </div>
              {/* Video Preview */}
              <div className="mt-3">
                <video 
                  src={currentVideoUrl} 
                  className="w-full max-w-md h-32 object-cover rounded border"
                  controls
                  muted
                >
                  Video preview is not supported.
                </video>
              </div>
            </div>
          )}

          {/* Video Upload */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload New Video
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleVideoUpload(file);
                  }
                }}
                disabled={videoUploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {videoUploading && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-600">
                    {uploadProgress < 40 ? 'Preparing...' : uploadProgress < 70 ? 'Uploading...' : uploadProgress < 90 ? 'Processing...' : 'Completing...'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500">{uploadProgress}%</p>
              </div>
            )}
          </div>
          
          <div className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded">
            <p className="font-medium mb-1">📌 Important Information:</p>
            <ul className="space-y-1">
              <li>• Maximum file size: 75MB</li>
              <li>• Supported formats: MP4, MOV, AVI</li>
              <li>• Video will automatically be displayed on the brand page</li>
              <li>• The content will refresh automatically once upload completes</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Vision Section Module
  const renderVisionModule = () => {
    const visionContent = getContentItem('brand', 'vision');

    const handleSave = async () => {
      try {
        await updateContentItem({
          id: visionContent?.id || `temp-brand-vision-${Date.now()}`,
          page: 'brand',
          section: 'vision',
          type: 'section',
          content: visionFormData.title,
          order: 0,
          metadata: visionFormData
        });
        await reloadContent();
        alert('Vision section saved successfully!');
      } catch (error) {
        alert('Error saving vision section!');
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
        <h3 className="text-lg font-semibold flex items-center">
          <i className="ri-eye-line mr-2"></i>
          Vision Section
        </h3>
        <input
          type="text"
          value={visionFormData.title}
          onChange={(e) => setVisionFormData({ ...visionFormData, title: e.target.value })}
          placeholder="Title (e.g. OUR)"
          className="w-full px-3 py-2 border rounded text-gray-900 bg-white"
        />
        <input
          type="text"
          value={visionFormData.subtitle}
          onChange={(e) => setVisionFormData({ ...visionFormData, subtitle: e.target.value })}
          placeholder="Subtitle - Bold (e.g. VISION)"
          className="w-full px-3 py-2 border rounded text-gray-900 bg-white"
        />
        <textarea
          value={visionFormData.description}
          onChange={(e) => setVisionFormData({ ...visionFormData, description: e.target.value })}
          placeholder="Description"
          rows={4}
          className="w-full px-3 py-2 border rounded text-gray-900 bg-white"
        />
        <MediaUploader
          type="image"
          currentUrl={visionFormData.image_url}
          label="Image"
          onUploadComplete={(url) => setVisionFormData({ ...visionFormData, image_url: url })}
        />
        <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded cursor-pointer whitespace-nowrap">Save</button>
      </div>
    );
  };

  // Story Section Module
  const renderStoryModule = () => {
    const storyContent = getContentItem('brand', 'story');

    const handleSave = async () => {
      try {
        await updateContentItem({
          id: storyContent?.id || `temp-brand-story-${Date.now()}`,
          page: 'brand',
          section: 'story',
          type: 'section',
          content: storyFormData.title,
          order: 0,
          metadata: storyFormData
        });
        await reloadContent();
        alert('Story section saved successfully!');
      } catch (error) {
        alert('Error saving story section!');
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
        <h3 className="text-lg font-semibold flex items-center">
          <i className="ri-book-line mr-2"></i>
          Story Section
        </h3>
        <input
          type="text"
          value={storyFormData.title}
          onChange={(e) => setStoryFormData({ ...storyFormData, title: e.target.value })}
          placeholder="Title (e.g. OUR)"
          className="w-full px-3 py-2 border rounded text-gray-900 bg-white"
        />
        <input
          type="text"
          value={storyFormData.subtitle}
          onChange={(e) => setStoryFormData({ ...storyFormData, subtitle: e.target.value })}
          placeholder="Subtitle - Bold (e.g. STORY)"
          className="w-full px-3 py-2 border rounded text-gray-900 bg-white"
        />
        <textarea
          value={storyFormData.description}
          onChange={(e) => setStoryFormData({ ...storyFormData, description: e.target.value })}
          placeholder="Description"
          rows={4}
          className="w-full px-3 py-2 border rounded text-gray-900 bg-white"
        />
        <MediaUploader
          type="image"
          currentUrl={storyFormData.image_url}
          label="Image"
          onUploadComplete={(url) => setStoryFormData({ ...storyFormData, image_url: url })}
        />
        <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded cursor-pointer whitespace-nowrap">Save</button>
      </div>
    );
  };

  // Architectural Design Module
  const renderArchitecturalModule = () => {
    const architecturalContent = content.filter(item => 
      item.page === 'brand' && item.section === 'architectural-design' && item.type === 'design'
    ).sort((a, b) => a.order - b.order);

    const handleSave = async () => {
      try {
        await updateContentItem({
          id: editingArchitectural?.id || `temp-brand-architectural-${Date.now()}`,
          page: 'brand',
          section: 'architectural-design',
          type: 'design',
          content: architecturalFormData.title,
          order: editingArchitectural?.order || architecturalContent.length,
          metadata: architecturalFormData
        });
        setEditingArchitectural(null);
        setArchitecturalFormData({ title: '', images: [] });
        await reloadContent();
        alert('Saved successfully!');
      } catch (error) {
        alert('Error saving data!');
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold">Architectural Design</h3>
          <button
            onClick={() => {
              setEditingArchitectural({ id: `temp-${Date.now()}`, order: architecturalContent.length });
              setArchitecturalFormData({ title: '', images: [] });
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>Add New Project
          </button>
        </div>

        <div className="space-y-4">
          {architecturalContent.map((design) => (
            <div key={design.id} className="bg-white p-4 rounded-lg border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">{design.metadata?.title}</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingArchitectural(design);
                      setArchitecturalFormData(design.metadata || { title: '', images: [] });
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm cursor-pointer whitespace-nowrap"
                  >
                    Edit
                  </button>
                  <button
                    onClick={async () => {
                      if (confirm('Are you sure you want to delete?')) {
                        await deleteContentItem(design.id);
                        await reloadContent();
                      }
                    }}
                    className="px-3 py-1 bg-red-600 text-white rounded text-sm cursor-pointer whitespace-nowrap"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {(design.metadata?.images || []).slice(0, 4).map((img: string, idx: number) => (
                  <img key={idx} src={img} alt="" className="w-full h-16 object-cover rounded" />
                ))}
              </div>
            </div>
          ))}
          {architecturalContent.length === 0 && (
            <div className="bg-gray-50 p-8 rounded-lg border-2 border-dashed text-center">
              <p className="text-gray-500">No projects added yet</p>
            </div>
          )}
        </div>

        {editingArchitectural && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold mb-4">Architectural Design</h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={architecturalFormData.title}
                  onChange={(e) => setArchitecturalFormData({ ...architecturalFormData, title: e.target.value })}
                  placeholder="Project Title"
                  className="w-full px-3 py-2 border rounded text-gray-900 bg-white"
                />
                
                {architecturalFormData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {architecturalFormData.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img src={img} alt="" className="w-full h-16 object-cover rounded" />
                        <button
                          onClick={() => setArchitecturalFormData({
                            ...architecturalFormData,
                            images: architecturalFormData.images.filter((_, i) => i !== idx)
                          })}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <MediaUploader
                  type="image"
                  currentUrl=""
                  label="Add Image"
                  onUploadComplete={(url) => setArchitecturalFormData({
                    ...architecturalFormData,
                    images: [...architecturalFormData.images, url]
                  })}
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={handleSave} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded cursor-pointer whitespace-nowrap">Save</button>
                <button onClick={() => setEditingArchitectural(null)} className="px-4 py-2 bg-gray-300 rounded cursor-pointer whitespace-nowrap">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Location & Surroundings Module
  const renderLocationSurroundingsModule = () => {
    const handleSave = async () => {
      try {
        const locationContent = getContentItem('brand', 'location-surroundings');
        await updateContentItem({
          id: locationContent?.id || `temp-brand-location-${Date.now()}`,
          page: 'brand',
          section: 'location-surroundings',
          type: 'location',
          content: 'Location & Surroundings',
          order: 0,
          metadata: locationFormData
        });
        await reloadContent();
        alert('Location & Surroundings saved successfully!');
      } catch (error) {
        alert('Error saving location & surroundings!');
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center">
          <i className="ri-map-pin-line mr-2"></i>
          Location & Surroundings Section
        </h3>

        <div className="space-y-6">
          {/* Location Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location Image
            </label>
            <MediaUploader
              type="image"
              currentUrl={locationFormData.image_url}
              label="Upload Image"
              onUploadComplete={(url) => setLocationFormData({ ...locationFormData, image_url: url })}
            />
          </div>

          {/* Google Maps URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Maps Embed URL
            </label>
            <textarea
              value={locationFormData.map_url}
              onChange={(e) => setLocationFormData({ ...locationFormData, map_url: e.target.value })}
              placeholder="https://www.google.com/maps/embed?pb=..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
            <p className="text-sm text-gray-500 mt-2">
              💡 Use the "Embed a map" option from Google Maps to get the iframe URL
            </p>
            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-medium mb-1">How to Get:</p>
              <ol className="text-xs text-blue-700 space-y-1 ml-4 list-decimal">
                <li>Search for your location on Google Maps</li>
                <li>Click the "Share" button</li>
                <li>Select the "Embed a map" tab</li>
                <li>Adjust map size if needed</li>
                <li>Copy the URL from src="..." in the iframe code</li>
              </ol>
            </div>
          </div>

          {/* Image Preview */}
          {locationFormData.image_url && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image Preview</label>
              <div className="w-full rounded-lg overflow-hidden border">
                <img src={locationFormData.image_url} alt="Location" className="w-full h-64 object-cover" />
              </div>
            </div>
          )}

          {/* Description Text (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description Text (Optional)
            </label>
            <textarea
              value={locationFormData.description}
              onChange={(e) => setLocationFormData({ ...locationFormData, description: e.target.value })}
              placeholder="You can write a description about the location and surroundings..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
            />
          </div>

          {/* Save Button */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={handleSave}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-save-line mr-2"></i>
              Save Changes
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start">
            <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <h4 className="font-medium text-blue-900 mb-2">About Location & Surroundings</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Only the Google Maps embed is displayed on the page</li>
                <li>• Map is rendered full width and centered</li>
                <li>• Description text is optional, you may leave it empty</li>
                <li>• Map height is set to 600px</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Architectural BG Video Module (top-level)
  function renderArchitecturalBgModuleView() {
    const bgItem = getContentItem('brand', 'architectural-bg');
    const currentUrl = bgItem?.video_url || bgItem?.content || '';
    const upsert = async (url: string) => {
      await updateContentItem({
        id: bgItem?.id || `temp-brand-architectural-bg-${Date.now()}`,
        page: 'brand',
        section: 'architectural-bg',
        type: 'video',
        content: 'Architectural BG',
        order: 0,
        video_url: url
      } as any);
      await reloadContent();
      alert('Architectural BG video saved');
    };
    return (
      <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
        <h3 className="text-lg font-semibold flex items-center">
          <i className="ri-video-line mr-2"></i>
          Architectural Design Background Video
        </h3>
        <MediaUploader type="video" currentUrl={currentUrl} label="Upload Video" onUploadComplete={(url) => upsert(url)} />
        {currentUrl && (
          <div className="flex gap-2">
            <button onClick={() => upsert('')} className="px-4 py-2 bg-red-600 text-white rounded cursor-pointer whitespace-nowrap">Remove Video</button>
          </div>
        )}
      </div>
    );
  }

  // Brand Video Slider Module (top-level)
  function renderBrandVideoSliderModuleView() {
    const item = getContentItem('brand', 'video-slider');
    const raw = Array.isArray(item?.metadata?.videos) ? item!.metadata.videos : [];
    const videos: { video: string; caption?: string }[] = raw
      .map((it: any) => {
        if (typeof it === 'string') return { video: it };
        const video = it?.video || it?.url || '';
        const caption = it?.caption || '';
        return video ? { video, caption } : null;
      })
      .filter(Boolean) as { video: string; caption?: string }[];

    const upsert = async (next: { addVideo?: string; removeIndex?: number; updateIndex?: number; updateCaption?: string }) => {
      const nextList = [...videos];
      if (typeof next.removeIndex === 'number') nextList.splice(next.removeIndex, 1);
      if (typeof next.updateIndex === 'number') {
        const idx = next.updateIndex;
        if (nextList[idx]) nextList[idx] = { ...nextList[idx], caption: next.updateCaption || '' };
      }
      if (next.addVideo) nextList.push({ video: next.addVideo, caption: '' });

      const payload: ContentItem = {
        id: item?.id || `temp-brand-video-slider-${Date.now()}`,
        page: 'brand',
        section: 'video-slider',
        type: 'section',
        content: 'Brand Video Slider',
        order: 0,
        metadata: { ...(item?.metadata || {}), videos: nextList }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Video slider saved successfully');
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Videos</h4>
          <MediaUploader type="video" label="Add New Video" onUploadComplete={(url) => upsert({ addVideo: url })} />
          <div className="grid grid-cols-1 gap-4 mt-4">
            {videos.length > 0 ? (
              videos.map((v, idx) => (
                <div key={idx} className="bg-gray-50 rounded border p-3">
                  <div className="flex flex-col md:flex-row gap-3">
                    <video src={v.video} className="w-full md:w-64 h-40 rounded border object-cover" controls />
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Video Caption</label>
                      <textarea id={`brand-video-caption-${idx}`} defaultValue={v.caption} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => {
                          const val = (document.getElementById(`brand-video-caption-${idx}`) as HTMLTextAreaElement)?.value || '';
                          upsert({ updateIndex: idx, updateCaption: val });
                        }} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Update</button>
                        <button onClick={() => upsert({ removeIndex: idx })} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">No videos added yet</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Brand Gallery Module (top-level)
  function renderBrandGalleryModule() {
    const item = getContentItem('brand', 'gallery');
    const raw = Array.isArray(item?.metadata?.images) ? item!.metadata.images : [];
    const images: { image: string; caption?: string }[] = raw
      .map((it: any) => {
        if (typeof it === 'string') return { image: it };
        const image = it?.image || it?.url || it?.src || '';
        const caption = it?.caption || it?.text || '';
        return image ? { image, caption } : null;
      })
      .filter(Boolean) as { image: string; caption?: string }[];

    const upsert = async (next: { addImage?: string; removeIndex?: number; updateIndex?: number; updateCaption?: string }) => {
      const nextList = [...images];
      if (typeof next.removeIndex === 'number') nextList.splice(next.removeIndex, 1);
      if (typeof next.updateIndex === 'number') {
        const idx = next.updateIndex;
        if (nextList[idx]) nextList[idx] = { ...nextList[idx], caption: next.updateCaption || '' };
      }
      if (next.addImage) nextList.push({ image: next.addImage, caption: '' });

      const payload: ContentItem = {
        id: item?.id || `temp-brand-gallery-${Date.now()}`,
        page: 'brand',
        section: 'gallery',
        type: 'gallery',
        content: 'Brand Gallery',
        order: 0,
        metadata: { ...(item?.metadata || {}), images: nextList }
      } as any;
      await updateContentItem(payload);
      await reloadContent();
      alert('Brand Gallery saved successfully');
    };

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Gallery</h4>
          <MediaUploader type="image" label="Add New Image" onUploadComplete={(url) => upsert({ addImage: url })} />
          <div className="grid grid-cols-1 gap-4 mt-4">
            {images.length > 0 ? (
              images.map((img, idx) => (
                <div key={idx} className="bg-gray-50 rounded border p-3">
                  <div className="flex flex-col md:flex-row gap-3">
                    <img src={img.image} className="w-full md:w-64 h-40 rounded border object-cover" />
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                      <textarea id={`brand-gallery-caption-${idx}`} defaultValue={img.caption} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white" />
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => {
                          const val = (document.getElementById(`brand-gallery-caption-${idx}`) as HTMLTextAreaElement)?.value || '';
                          upsert({ updateIndex: idx, updateCaption: val });
                        }} className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Update</button>
                        <button onClick={() => upsert({ removeIndex: idx })} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">No images added yet</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const renderArchitecturalBgModule = renderArchitecturalBgModuleView;
  const renderBrandVideoSliderModule = renderBrandVideoSliderModuleView;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-600">Loading...</div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Brand Content Management</h2>
        <button
          onClick={reloadContent}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          <i className="ri-refresh-line mr-2"></i>Refresh
        </button>
      </div>

      <div className="grid grid-cols-6 gap-4">
        {/* Module Sidebar */}
        <div className="col-span-1 space-y-2">
          {brandModules.map((module) => (
            <button
              key={module.id}
              onClick={() => setActiveModule(module.id)}
              className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                activeModule === module.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className={`${module.icon} mr-2`}></i>
              <span className="text-sm">{module.name}</span>
            </button>
          ))}
        </div>

        {/* Module Content */}
        <div className="col-span-5">
          {activeModule === 'brand-hero' && renderHeroModule()}
          {activeModule === 'brand-hero-text' && renderBrandHeroTextModule()}
          {activeModule === 'brand-about' && renderAboutModule()}
          {activeModule === 'brand-residences' && renderResidencesModule()}
          {activeModule === 'brand-location-new' && renderLocationNewModule()}
          {activeModule === 'brand-amenities' && renderAmenitiesModule()}
          {activeModule === 'brand-investment' && renderInvestmentModule()}
          {activeModule === 'brand-contact' && renderBrandContactSectionModule()}
          {activeModule === 'brand-vision' && renderVisionModule()}
          {activeModule === 'brand-story' && renderStoryModule()}
          {activeModule === 'brand-architectural' && renderArchitecturalModule()}
          {activeModule === 'brand-architectural-bg' && renderArchitecturalBgModule()}
          {activeModule === 'brand-video-slider' && renderBrandVideoSliderModule()}
          {activeModule === 'brand-gallery' && renderBrandGalleryModule()}
          {activeModule === 'location-surroundings' && renderLocationSurroundingsModule()}
        </div>
      </div>
    </div>
  );
}
